import { create } from 'zustand';
import {
  Warband,
  SpellcasterDTO,
  SoldiersDTO,
  SpellcastersAndSpellsDTO,
  WarbandPage,
  UIState,
  BuffOrDebuff,
  VaultItemDTO,
  BaseResource,
  Spell,
  WordEffect,
  WarbandLevelUp,
} from '../types';
import { useReferenceDataStore } from './referenceDataStore';
import { spellService } from '../services/spellService';
import {
  loadWarbandData,
  hydrateWarbandState,
  buildHydrationMaps,
  persistCurrentState,
  saveWarbandIndex,
} from '../services/localStorageService';
import { allSpells as spellsData } from '../services/spellService';
import { queueSyncEntity } from '../services/syncCoordinator';

interface WarbandStore {
  // Navigation state
  selectedWarbandId: string | null;
  currentPage: WarbandPage;
  lastNonManagePage: WarbandPage;
  manageTabIndex: number;
  playTabIndex: number;
  isMinified: boolean;

  // UI state
  ui: UIState;

  // Data state
  warbands: Warband[];
  warbandsLoaded: boolean;
  wizardData: SpellcasterDTO | null;
  apprenticeData: SpellcasterDTO | null;
  soldiersData: SoldiersDTO | null;
  spellcastersAndSpells: SpellcastersAndSpellsDTO | null;
  availableBuffs: BuffOrDebuff[];
  warbandBuffs: BuffOrDebuff[];
  wordEffects: WordEffect[];
  vaultItems: VaultItemDTO[];
  baseResources: BaseResource[];
  baseResourceCostsPaid: Record<string, number>;
  allAvailableSpells: Spell[];
  primarySpells: Spell[];
  alignedSpells: Spell[][];
  neutralSpells: Spell[][];
  lastManageDataWarbandId: string | null;

  // Loading states
  isLoading: boolean;
  error: string | null;

  // Navigation actions
  selectWarband: (
    id: string | null,
    options?: {
      currentPage?: WarbandPage;
      manageTabIndex?: number;
      playTabIndex?: number;
    }
  ) => void;
  navigateToPage: (page: WarbandPage) => void;
  setManageTabIndex: (tabIndex: number) => void;
  setPlayTabIndex: (tabIndex: number) => void;
  setIsMinified: (value: boolean) => void;

  // UI actions
  setMenuOpen: (open: boolean) => void;
  setPageSelectorOpen: (open: boolean) => void;
  toggleMenu: () => void;
  togglePageSelector: () => void;
  openCreateWarbandModal: () => void;
  closeCreateWarbandModal: () => void;
  openCreateWizardModal: () => void;
  closeCreateWizardModal: () => void;
  openCreateApprenticeModal: () => void;
  closeCreateApprenticeModal: () => void;
  openCreateSoldierModal: (temporary?: boolean) => void;
  closeCreateSoldierModal: () => void;
  openAddSpellModal: (fromGrimoire?: boolean) => void;
  closeAddSpellModal: () => void;
  openAddStartingSpellsModal: () => void;
  closeAddStartingSpellsModal: () => void;
  openCustomBestiaryModal: () => void;
  closeCustomBestiaryModal: () => void;
  openCustomEquipmentModal: () => void;
  closeCustomEquipmentModal: () => void;
  openCustomBuffsModal: () => void;
  closeCustomBuffsModal: () => void;

  // Data actions
  setWarbands: (warbands: Warband[]) => void;
  setWizardData: (wizard: SpellcasterDTO | null) => void;
  setApprenticeData: (apprentice: SpellcasterDTO | null) => void;
  setSoldiersData: (soldiers: SoldiersDTO | null) => void;
  setSpellcastersAndSpells: (data: SpellcastersAndSpellsDTO | null) => void;
  setAvailableBuffs: (buffs: BuffOrDebuff[]) => void;
  setWarbandBuffs: (buffs: BuffOrDebuff[]) => void;
  setWordEffects: (effects: WordEffect[]) => void;
  setVaultItems: (items: VaultItemDTO[]) => void;
  updateVaultItemMemberId: (instanceId: string, memberId: string | null) => void;
  setBaseResources: (resources: BaseResource[]) => void;
  setBaseResourceCostsPaid: (costs: Record<string, number>) => void;
  setBaseId: (baseId: string | null) => void;
  levelUps: WarbandLevelUp[];
  levelUpsVersion: number;
  setLevelUps: (levelUps: WarbandLevelUp[]) => void;
  bumpLevelUpsVersion: () => void;

  // Fetch actions
  fetchPlayWarbandData: (warbandId: string) => Promise<void>;
  fetchManageWarbandData: (warbandId: string) => Promise<void>;
}

const sanitizeTabIndex = (tabIndex: number | undefined): number => {
  if (typeof tabIndex !== 'number' || Number.isNaN(tabIndex)) {
    return 0;
  }

  if (tabIndex < 0) {
    return 0;
  }

  return Math.floor(tabIndex);
};

export const useWarbandStore = create<WarbandStore>((set, get) => ({
  // Navigation state
  selectedWarbandId: null,
  currentPage: 'manage',
  lastNonManagePage: 'pregame',
  manageTabIndex: 0,
  playTabIndex: 0,
  isMinified: localStorage.getItem('frostgrave:ui:minified') !== 'false',

  // UI state
  ui: {
    menuOpen: false,
    pageSelectorOpen: false,
    createWarbandModalOpen: false,
    createWizardModalOpen: false,
    createApprenticeModalOpen: false,
    createSoldierModalOpen: false,
    createSoldierTemporary: false,
    addSpellModalOpen: false,
    addSpellModalFromGrimoire: false,
    addStartingSpellsModalOpen: false,
    customBestiaryModalOpen: false,
    customEquipmentModalOpen: false,
    customBuffsModalOpen: false,
  },

  // Data state
  warbands: [],
  warbandsLoaded: false,
  wizardData: null,
  apprenticeData: null,
  soldiersData: null,
  spellcastersAndSpells: null,
  availableBuffs: [],
  warbandBuffs: [],
  wordEffects: [],
  vaultItems: [],
  baseResources: [],
  baseResourceCostsPaid: {},
  allAvailableSpells: [],
  primarySpells: [],
  alignedSpells: [],
  neutralSpells: [],
  lastManageDataWarbandId: null,

  // Loading states
  isLoading: false,
  error: null,
  levelUps: [],
  levelUpsVersion: 0,

  // Navigation actions
  selectWarband: (id: string | null, options) => {
    const previousWarbandId = get().selectedWarbandId;

    // Clear spell cache for previous warband if changing
    if (previousWarbandId && previousWarbandId !== id) {
      spellService.clearWarbandCache(previousWarbandId);
    }

    // Set navigation/UI state and mark as loading immediately, but do NOT
    // null out warband data yet — the dirty flush needs the current data
    // for reconciliation (replayPendingChanges).
    set({
      selectedWarbandId: id,
      lastManageDataWarbandId: null,
      currentPage: options?.currentPage ?? 'manage',
      manageTabIndex: sanitizeTabIndex(options?.manageTabIndex),
      playTabIndex: sanitizeTabIndex(options?.playTabIndex),
      ui: {
        menuOpen: false,
        pageSelectorOpen: false,
        createWarbandModalOpen: false,
        createWizardModalOpen: false,
        createApprenticeModalOpen: false,
        createSoldierModalOpen: false,
        createSoldierTemporary: false,
        addSpellModalOpen: false,
        addSpellModalFromGrimoire: false,
        addStartingSpellsModalOpen: false,
        customBestiaryModalOpen: false,
        customEquipmentModalOpen: false,
        customBuffsModalOpen: false,
      },
      isLoading: !!id,
      error: null,
    });

    void (async () => {
      set({
        wizardData: null,
        apprenticeData: null,
        soldiersData: null,
        spellcastersAndSpells: null,
        availableBuffs: [],
        warbandBuffs: [],
        wordEffects: [],
        vaultItems: [],
        baseResources: [],
        baseResourceCostsPaid: {},
        allAvailableSpells: [],
        primarySpells: [],
        alignedSpells: [],
        neutralSpells: [],
        levelUps: [],
      });

      if (!id) {
        set({ isLoading: false });
        return;
      }

      if (get().selectedWarbandId !== id) {
        return;
      }

      spellService.clearWarbandCache(id);
      await get().fetchPlayWarbandData(id);
    })();
  },

  navigateToPage: (page: WarbandPage) => {
    set({
      currentPage: page,
      ...(page !== 'manage' ? { lastNonManagePage: page } : {}),
      ui: { ...get().ui, pageSelectorOpen: false },
    });
  },

  setManageTabIndex: (tabIndex: number) => {
    set({ manageTabIndex: sanitizeTabIndex(tabIndex) });
  },

  setPlayTabIndex: (tabIndex: number) => {
    set({ playTabIndex: sanitizeTabIndex(tabIndex) });
  },

  setIsMinified: (value: boolean) => {
    localStorage.setItem('frostgrave:ui:minified', String(value));
    set({ isMinified: value });
  },

  // UI actions
  setMenuOpen: (open: boolean) => {
    set({ ui: { ...get().ui, menuOpen: open } });
  },

  setPageSelectorOpen: (open: boolean) => {
    set({ ui: { ...get().ui, pageSelectorOpen: open } });
  },

  toggleMenu: () => {
    const currentState = get().ui.menuOpen;
    set({ ui: { ...get().ui, menuOpen: !currentState } });
  },

  togglePageSelector: () => {
    const currentState = get().ui.pageSelectorOpen;
    set({ ui: { ...get().ui, pageSelectorOpen: !currentState } });
  },

  openCreateWarbandModal: () => {
    set({ ui: { ...get().ui, createWarbandModalOpen: true } });
  },

  closeCreateWarbandModal: () => {
    set({ ui: { ...get().ui, createWarbandModalOpen: false } });
  },

  openCreateWizardModal: () => {
    set({ ui: { ...get().ui, createWizardModalOpen: true } });
  },

  closeCreateWizardModal: () => {
    set({ ui: { ...get().ui, createWizardModalOpen: false } });
  },

  openCreateApprenticeModal: () => {
    set({ ui: { ...get().ui, createApprenticeModalOpen: true } });
  },

  closeCreateApprenticeModal: () => {
    set({ ui: { ...get().ui, createApprenticeModalOpen: false } });
  },

  openCreateSoldierModal: (temporary = false) => {
    set({ ui: { ...get().ui, createSoldierModalOpen: true, createSoldierTemporary: temporary } });
  },

  closeCreateSoldierModal: () => {
    set({ ui: { ...get().ui, createSoldierModalOpen: false, createSoldierTemporary: false } });
  },

  openAddSpellModal: (fromGrimoire = false) => {
    set({ ui: { ...get().ui, addSpellModalOpen: true, addSpellModalFromGrimoire: fromGrimoire } });
  },

  closeAddSpellModal: () => {
    set({ ui: { ...get().ui, addSpellModalOpen: false, addSpellModalFromGrimoire: false } });
  },

  openAddStartingSpellsModal: () => {
    set({ ui: { ...get().ui, addStartingSpellsModalOpen: true } });
  },

  closeAddStartingSpellsModal: () => {
    set({ ui: { ...get().ui, addStartingSpellsModalOpen: false } });
  },

  openCustomBestiaryModal: () => {
    set({ ui: { ...get().ui, customBestiaryModalOpen: true } });
  },

  closeCustomBestiaryModal: () => {
    set({ ui: { ...get().ui, customBestiaryModalOpen: false } });
  },

  openCustomEquipmentModal: () => {
    set({ ui: { ...get().ui, customEquipmentModalOpen: true } });
  },

  closeCustomEquipmentModal: () => {
    set({ ui: { ...get().ui, customEquipmentModalOpen: false } });
  },

  openCustomBuffsModal: () => {
    set({ ui: { ...get().ui, customBuffsModalOpen: true } });
  },

  closeCustomBuffsModal: () => {
    set({ ui: { ...get().ui, customBuffsModalOpen: false } });
  },

  // Data actions
  setWarbands: (warbands: Warband[]) => {
    set({ warbands, warbandsLoaded: true });
    saveWarbandIndex(warbands);
  },

  setWizardData: (wizard: SpellcasterDTO | null) => {
    set({ wizardData: wizard });
  },

  setApprenticeData: (apprentice: SpellcasterDTO | null) => {
    set({ apprenticeData: apprentice });
  },

  setSoldiersData: (soldiers: SoldiersDTO | null) => {
    set({ soldiersData: soldiers });
  },

  setSpellcastersAndSpells: (data: SpellcastersAndSpellsDTO | null) => {
    set({ spellcastersAndSpells: data });
  },

  setAvailableBuffs: (buffs: BuffOrDebuff[]) => {
    set({ availableBuffs: buffs });
  },

  setWarbandBuffs: (buffs: BuffOrDebuff[]) => {
    set({ warbandBuffs: buffs });
  },

  setWordEffects: (effects: WordEffect[]) => {
    set({ wordEffects: effects });
  },

  setVaultItems: (items: VaultItemDTO[]) => {
    set({ vaultItems: items });
  },

  updateVaultItemMemberId: (instanceId: string, memberId: string | null) => {
    set((state) => ({
      vaultItems: state.vaultItems.map((v) =>
        v.instanceId === instanceId ? { ...v, warbandMemberId: memberId } : v
      ),
    }));
  },

  setBaseResources: (resources: BaseResource[]) => {
    set({ baseResources: resources });
  },

  setBaseResourceCostsPaid: (costs: Record<string, number>) => {
    set({ baseResourceCostsPaid: costs });
  },

  setBaseId: (baseId: string | null) => {
    const warbands = get().warbands;
    const selectedId = get().selectedWarbandId;
    if (selectedId) {
      set({
        warbands: warbands.map((w) =>
          w.id === selectedId ? { ...w, baseId } : w
        ),
      });
    }
  },

  setLevelUps: (levelUps: WarbandLevelUp[]) => {
    set({ levelUps });
  },

  bumpLevelUpsVersion: () => {
    const id = get().selectedWarbandId;
    const freshLevelUps = id ? (loadWarbandData(id)?.levelUps ?? []) : [];
    set((state) => ({ levelUpsVersion: state.levelUpsVersion + 1, levelUps: freshLevelUps }));
  },

  // Fetch actions
  fetchPlayWarbandData: async (warbandId: string) => {
    set({ isLoading: true, error: null });
    try {
      const stored = loadWarbandData(warbandId);
      if (!stored) {
        if (get().selectedWarbandId === warbandId) set({ isLoading: false });
        return;
      }

      const refState = useReferenceDataStore.getState();
      const maps = buildHydrationMaps({
        weapons: refState.weapons,
        armours: refState.armours,
        buffs: refState.buffs,
        treasureItems: refState.treasureItems,
        baseResources: refState.allBaseResources,
        injuries: refState.injuries,
      });

      const hydrated = hydrateWarbandState(stored, spellsData, refState.buffs, maps);

      if (get().selectedWarbandId !== warbandId) return;

      set({
        wizardData: hydrated.wizard,
        apprenticeData: hydrated.apprentice,
        soldiersData: { soldiers: hydrated.soldiers },
        spellcastersAndSpells: {
          wizard: hydrated.wizard,
          apprentice: hydrated.apprentice,
          warbandSpells: hydrated.selectedSpells,
        },
        availableBuffs: hydrated.availableBuffs,
        warbandBuffs: hydrated.warbandBuffs,
        wordEffects: hydrated.wordEffects,
        vaultItems: hydrated.vaultItems,
        baseResources: hydrated.selectedBaseResources,
        baseResourceCostsPaid: hydrated.baseResourceCostsPaid,
        allAvailableSpells: hydrated.allAvailableSpells,
        primarySpells: hydrated.primarySpells,
        alignedSpells: hydrated.alignedSpells,
        neutralSpells: hydrated.neutralSpells,
        levelUps: stored.levelUps ?? [],
        lastManageDataWarbandId: warbandId,
        isLoading: false,
      });
    } catch (err) {
      if (get().selectedWarbandId !== warbandId) return;
      const errorMessage = err instanceof Error ? err.message : 'Failed to load warband data';
      set({ error: errorMessage, isLoading: false });
    }
  },

  fetchManageWarbandData: async (warbandId: string) => {
    // Always reload from localStorage — reads are synchronous and cheap.
    await get().fetchPlayWarbandData(warbandId);
  },
}));

// ---------------------------------------------------------------------------
// Persist warband data to localStorage whenever in-memory state changes.
// This replaces the API batch-update flush mechanism.
// ---------------------------------------------------------------------------

const dataChanged = (
  state: WarbandStore,
  prevState: WarbandStore
): boolean =>
  state.wizardData !== prevState.wizardData ||
  state.apprenticeData !== prevState.apprenticeData ||
  state.soldiersData !== prevState.soldiersData ||
  state.vaultItems !== prevState.vaultItems ||
  state.baseResources !== prevState.baseResources ||
  state.baseResourceCostsPaid !== prevState.baseResourceCostsPaid ||
  state.warbandBuffs !== prevState.warbandBuffs ||
  state.wordEffects !== prevState.wordEffects ||
  state.spellcastersAndSpells !== prevState.spellcastersAndSpells;

useWarbandStore.subscribe((state, prevState) => {
  const id = state.selectedWarbandId;
  if (!id || state.isLoading) return;
  if (dataChanged(state, prevState)) {
    persistCurrentState(id);
    queueSyncEntity(`warband:${id}`);
  }
});
